import type { typePlayerSearch } from "../../routeState/playerSearch";
import type { typePlayerInjuryStatus } from "./playerProfileView";

export type typePlayerProfileView = {
  playerId: string;
  name: string;
  teamId: string | null;
  teamName: string | null;
  teamAbbreviation: string | null;
  jerseyNumber: string | null;
  position: string | null;
  injuryStatus: typePlayerInjuryStatus | null;
};

/** One player row as /team/roster-get sends it. Structural, matching the generated SDK type. */
export type typePlayerRosterPlayer = {
  playerId: string;
  name: string;
  jerseyNumber?: string | null;
  position?: string | null;
  heightLabel?: string | null;
  weightLabel?: string | null;
  age?: number | null;
  college?: string | null;
  experienceLabel?: string | null;
  headshotUrl?: string | null;
};

export type typePlayerRosterGroup = {
  players: readonly typePlayerRosterPlayer[];
};

/** The stored record for the player on this page. Blank strings are already null. */
export type typePlayerRecord = {
  playerId: string;
  jerseyNumber: string | null;
  position: string | null;
  heightLabel: string | null;
  weightLabel: string | null;
  age: number | null;
  college: string | null;
  experienceLabel: string | null;
  headshotUrl: string | null;
};

/** One entry as /team/injuries-get sends it. Structural. */
export type typePlayerInjuryEntry = {
  playerId?: string | null;
  status: typePlayerInjuryStatus;
};

/** What the server reads add to the URL view. */
export type typePlayerProfileDetails = {
  record: typePlayerRecord | null;
  /** The URL's status first, then the team report's. Null when neither has one. */
  injuryStatus: typePlayerInjuryStatus | null;
  /** True only when the team's injury report read answered hasReport: true. */
  hasInjuryReport: boolean;
  /** True while a roster or injuries read is in flight. */
  isLoading: boolean;
};

export const EMPTY_PLAYER_PROFILE_DETAILS: typePlayerProfileDetails = {
  record: null,
  injuryStatus: null,
  hasInjuryReport: false,
  isLoading: false,
};

function blankToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function normalizePersonName(name: string): string {
  return name.trim().split(/\s+/).join(" ").toLowerCase();
}

function toPlayerRecord(player: typePlayerRosterPlayer): typePlayerRecord {
  return {
    playerId: player.playerId,
    jerseyNumber: blankToNull(player.jerseyNumber),
    position: blankToNull(player.position),
    heightLabel: blankToNull(player.heightLabel),
    weightLabel: blankToNull(player.weightLabel),
    age: typeof player.age === "number" ? player.age : null,
    college: blankToNull(player.college),
    experienceLabel: blankToNull(player.experienceLabel),
    headshotUrl: blankToNull(player.headshotUrl),
  };
}

function trimToNull(value: string | undefined): string | null {
  if (value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** Exactly the four wire values. Anything else is null. */
export function parsePlayerInjuryStatus(
  value: string | undefined,
): typePlayerInjuryStatus | null {
  if (
    value === "out" ||
    value === "doubtful" ||
    value === "questionable" ||
    value === "probable"
  ) {
    return value;
  }
  return null;
}

/** The view, or null when the caller named no player. */
export function readPlayerSearch(
  search: typePlayerSearch,
): typePlayerProfileView | null {
  const playerId = trimToNull(search.playerId);
  const name = trimToNull(search.name);
  if (playerId === null || name === null) return null;

  return {
    playerId,
    name,
    teamId: trimToNull(search.teamId),
    teamName: trimToNull(search.teamName),
    teamAbbreviation: trimToNull(search.teamAbbreviation),
    jerseyNumber: trimToNull(search.jerseyNumber),
    position: trimToNull(search.position),
    injuryStatus: parsePlayerInjuryStatus(search.injuryStatus),
  };
}

export function findRosterPlayer(input: {
  groups: readonly typePlayerRosterGroup[];
  playerId: string;
  name: string;
}): typePlayerRecord | null {
  for (const group of input.groups) {
    for (const player of group.players) {
      if (player.playerId === input.playerId) return toPlayerRecord(player);
    }
  }
  const wanted = normalizePersonName(input.name);
  const matches: typePlayerRosterPlayer[] = [];
  for (const group of input.groups) {
    for (const player of group.players) {
      if (normalizePersonName(player.name) === wanted) matches.push(player);
    }
  }
  const [only] = matches;
  return matches.length === 1 && only !== undefined
    ? toPlayerRecord(only)
    : null;
}

export function resolvePlayerInjuryStatus(input: {
  fromSearch: typePlayerInjuryStatus | null;
  injuries: readonly typePlayerInjuryEntry[];
  playerIds: readonly string[];
}): typePlayerInjuryStatus | null {
  if (input.fromSearch !== null) return input.fromSearch;
  for (const entry of input.injuries) {
    if (
      typeof entry.playerId === "string" &&
      input.playerIds.includes(entry.playerId)
    ) {
      return entry.status;
    }
  }
  return null;
}

export function mergePlayerProfileView(input: {
  view: typePlayerProfileView;
  details: typePlayerProfileDetails;
}): typePlayerProfileView {
  const { view, details } = input;
  return {
    ...view,
    jerseyNumber: view.jerseyNumber ?? details.record?.jerseyNumber ?? null,
    position: view.position ?? details.record?.position ?? null,
    injuryStatus: details.injuryStatus ?? view.injuryStatus,
  };
}
